import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import Pagination from '../components/commons/Pagination'
import DialogStoreOwner from '../components/dialogs/DialogStoreOwner'
import DialogStoreListings from '../components/dialogs/DialogStoreListings'
import { getShopifyProducts, getShopifyStores, setStoreBlocked, syncStore } from '../firebase/shopify'

const ShopifyManagement = () => {
    const [products, setProducts] = useState([])
    const [loading, setLoading] = useState(true)
    const [currentPage, setCurrentPage] = useState(1)
    const itemsPerPage = 8

    const [stores, setStores] = useState([])
    const [loadingStores, setLoadingStores] = useState(true)
    const [editingStore, setEditingStore] = useState(null)
    // Which store is mid-sync, so only that row's button shows a spinner.
    const [syncingShop, setSyncingShop] = useState(null)
    // Which store's block state is being written, so only that row's button
    // is disabled while it lands.
    const [blockingShop, setBlockingShop] = useState(null)
    const [listingsShop, setListingsShop] = useState(null)

    const refreshStores = async () => {
        setLoadingStores(true)
        try {
            setStores(await getShopifyStores())
        } catch (error) {
            console.error('Failed to load connected Shopify stores:', error)
        } finally {
            setLoadingStores(false)
        }
    }

    useEffect(() => {
        refreshStores()
    }, [])

    // Products carry their store's role id as seller_id; this maps it back to
    // something readable without another round trip per row.
    const storeBySellerId = new Map(stores.map((store) => [store.seller_id, store]))

    const refreshProducts = async () => {
        setLoading(true)
        const rows = await getShopifyProducts()
        setProducts(rows)
        setCurrentPage(1)
        setLoading(false)
    }

    useEffect(() => {
        refreshProducts()
    }, [])

    // Blocking takes a whole store off the marketplace: its listings stop
    // appearing and nothing from it can be bought, whatever the owner's own
    // per-listing switches say.
    const handleToggleBlocked = async (store) => {
        const blocking = !store.blocked
        const confirmed = window.confirm(
            blocking
                ? `Block ${store.shop_domain}? Its ${store.listings?.total || 0} listings will stop appearing on the marketplace and cannot be bought until you unblock it.`
                : `Unblock ${store.shop_domain}? Its listings will return to the marketplace.`
        )
        if (!confirmed) return

        setBlockingShop(store.shop_domain)
        try {
            await setStoreBlocked(store.shop_domain, blocking)
            toast.success(`${store.shop_domain} ${blocking ? 'blocked' : 'unblocked'}`)
            await refreshStores()
        } catch (error) {
            console.error('Failed to update store block state:', error)
            toast.error(error.message || 'Failed to update the store')
        } finally {
            setBlockingShop(null)
        }
    }

    const currentProducts = products.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

    const handleSyncStore = async (shop) => {
        setSyncingShop(shop)

        try {
            const payload = await syncStore(shop)
            toast.success(`${shop}: ${payload?.count || 0} products synced`)
            await refreshProducts()
            await refreshStores()
        } catch (error) {
            console.error('Shopify sync failed:', error)
            toast.error(error.message || 'Shopify sync failed')
        } finally {
            setSyncingShop(null)
        }
    }

    return (
        <div className="px-10 py-8">
            <div className="flex items-center justify-between gap-4">
                <h1 className="text-primary text-[36px]">Shopify Products</h1>

            </div>

            <div className="mt-8">
                <h2 className="text-white text-[20px] mb-3">Connected stores</h2>

                {loadingStores ? (
                    <p className="text-sm text-[#a0a0a0]">Loading connected stores...</p>
                ) : stores.length === 0 ? (
                    <p className="text-sm text-[#a0a0a0]">
                        No Shopify stores connected yet. Install the app on a store to connect it.
                    </p>
                ) : (
                    <div className="w-full overflow-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-[#2E2E2E] rounded-[4px] py-3 text-sm text-[#717171]">
                                    <th className="py-3 px-3 text-left">Store</th>
                                    <th className="py-3 px-3 text-left">Owner</th>
                                    <th className="py-3 px-3 text-left">Multiplier</th>
                                    <th className="py-3 px-3 text-left">Listings</th>
                                    <th className="py-3 px-3 text-left">Status</th>
                                    <th className="py-3 px-3 text-left">Outstanding</th>
                                    <th className="py-3 px-3 text-left"></th>
                                </tr>
                            </thead>
                            <tbody className="text-white text-sm">
                                {stores.map((store) => (
                                    <tr key={store.shop_domain} className="h-[60px] hover:bg-[#2F2F2F]">
                                        <td className="py-3 px-3">
                                            {store.shop_domain}
                                            {!store.connected && (
                                                <span className="text-yellow-400 text-xs ml-2">(token missing)</span>
                                            )}
                                        </td>
                                        <td className="py-3 px-3">
                                            {store.owner_uid
                                                ? store.owner_display_name
                                                : <span className="text-yellow-400">Unassigned</span>}
                                        </td>
                                        <td className="py-3 px-3">
                                            {Number(store.price_multiplier ?? 1) === 1
                                                ? <span className="text-[#717171]">1 (default)</span>
                                                : <span>&times;{store.price_multiplier}</span>}
                                        </td>
                                        <td className="py-3 px-3">
                                            <button
                                                className="underline hover:text-primary"
                                                onClick={() => setListingsShop(store.shop_domain)}
                                                title="View this store's listings"
                                            >
                                                {store.listings?.total || 0}
                                            </button>
                                            <div className="text-[#717171] text-xs">
                                                {store.listings?.visible || 0} listed
                                                {(store.listings?.hidden || 0) > 0 && `, ${store.listings.hidden} hidden`}
                                                {(store.listings?.out_of_stock || 0) > 0 && `, ${store.listings.out_of_stock} out of stock`}
                                            </div>
                                        </td>
                                        <td className="py-3 px-3">
                                            {store.blocked
                                                ? <span className="text-red-400 uppercase text-xs">Blocked</span>
                                                : <span className="text-green-400 uppercase text-xs">Active</span>}
                                        </td>
                                        <td className="py-3 px-3">
                                            {store.handover?.unshipped_orders > 0 ? (
                                                <span className="text-yellow-400">
                                                    {store.handover.unshipped_orders} unshipped
                                                </span>
                                            ) : store.handover?.open_orders > 0 ? (
                                                <span className="text-[#a0a0a0]">
                                                    {store.handover.open_orders} in transit
                                                </span>
                                            ) : (
                                                <span className="text-[#717171]">None</span>
                                            )}
                                        </td>
                                        <td className="py-3 px-3">
                                            <div className="flex items-center gap-2 justify-end">
                                                <button
                                                    className="px-4 py-2 text-sm bg-white text-black rounded-[33px] disabled:opacity-60"
                                                    onClick={() => handleSyncStore(store.shop_domain)}
                                                    disabled={!store.connected || syncingShop !== null}
                                                    title={store.connected ? '' : 'This store has no access token; reinstall the app on it first.'}
                                                >
                                                    {syncingShop === store.shop_domain ? 'Syncing...' : 'Re-sync'}
                                                </button>
                                                <button
                                                    className="px-4 py-2 text-sm bg-[#1F1F1F] border border-[#3A3A3A] text-white rounded-[33px]"
                                                    onClick={() => setEditingStore(store)}
                                                >
                                                    Change owner
                                                </button>
                                                <button
                                                    className="px-4 py-2 text-sm bg-[#1F1F1F] border border-[#3A3A3A] text-white rounded-[33px]"
                                                    onClick={() => setListingsShop(store.shop_domain)}
                                                >
                                                    Listings
                                                </button>
                                                <button
                                                    className={`px-4 py-2 text-sm rounded-[33px] border disabled:opacity-60 ${store.blocked
                                                        ? 'bg-[#1F1F1F] border-[#3A3A3A] text-white'
                                                        : 'bg-[#1F1F1F] border-red-500/60 text-red-400'}`}
                                                    onClick={() => handleToggleBlocked(store)}
                                                    disabled={blockingShop !== null}
                                                >
                                                    {blockingShop === store.shop_domain
                                                        ? 'Saving...'
                                                        : store.blocked ? 'Unblock' : 'Block'}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="mt-10">
                <h2 className="text-white text-[20px] mb-3">Synced products</h2>

                {loading ? (
                    <div className="my-10">
                        <div className="flex items-center justify-center h-64">
                            <div className="flex flex-col items-center">
                                <img src="/assets/icons/icon-loading.svg" alt="loading" className="w-8 h-8 animate-spin mb-4" />
                                <p className="text-white text-lg">Loading Shopify products...</p>
                            </div>
                        </div>
                    </div>
                ) : products.length > 0 ? (
                    <div className="w-full overflow-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-[#2E2E2E] rounded-[4px] py-3 text-sm text-[#717171]">
                                    <th className="py-3 px-3 text-left">Product</th>
                                    <th className="py-3 px-3 text-left">Store / owner</th>
                                    <th className="py-3 px-3 text-left">SKU</th>
                                    <th className="py-3 px-3 text-left">Price</th>
                                    <th className="py-3 px-3 text-left">Inventory</th>
                                    <th className="py-3 px-3 text-left">Status</th>
                                </tr>
                            </thead>
                            <tbody className="text-white space-y-1 text-sm">
                                {currentProducts.map((product) => {
                                    const firstVariant = product?.variants?.[0] || {}
                                    const inventoryTotal = (product?.variants || []).reduce((sum, variant) => sum + Number(variant.inventory_quantity || 0), 0)
                                    const store = storeBySellerId.get(product?.seller_id)
                                    const sellerName = store
                                        ? `${store.shop_domain} (${store.owner_display_name})`
                                        : (product?.shop_domain || product?.seller_id || 'Unassigned')

                                    return (
                                        <tr key={product.id} className="h-[70px] hover:bg-[#2F2F2F] p-2">
                                            <td className="py-3 px-3">
                                                <div className="flex items-center gap-3">
                                                    <img src={product.image?.src || '/assets/images/image_item_default.png'} alt="" className="w-[40px] h-[40px] object-cover" />
                                                    <span className="uppercase">{product.title}</span>
                                                </div>
                                            </td>
                                            <td className="py-3 px-3">{sellerName}</td>
                                            <td className="py-3 px-3">{firstVariant.sku || 'N/A'}</td>
                                            <td className="py-3 px-3">${Number(firstVariant.price || 0).toFixed(2)}</td>
                                            <td className="py-3 px-3">{inventoryTotal}</td>
                                            <td className="py-3 px-3">{product.status || 'active'}</td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>

                        <div className="mt-6">
                            <Pagination
                                itemsPerPage={itemsPerPage}
                                totalItems={products.length}
                                onPageChange={(value) => setCurrentPage(value)}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="text-white text-lg text-center mt-8 w-full">
                        No Shopify products synced yet
                    </div>
                )}
            </div>

            <DialogStoreListings
                shop={listingsShop}
                open={Boolean(listingsShop)}
                onClose={() => setListingsShop(null)}
            />

            {editingStore && (
                <DialogStoreOwner
                    store={editingStore}
                    open={Boolean(editingStore)}
                    onClose={() => setEditingStore(null)}
                    onSaved={() => {
                        refreshStores()
                        refreshProducts()
                    }}
                />
            )}
        </div>
    )
}

export default ShopifyManagement
